import TokenService from "./storage.service";
import router from "../router";
import axios from "axios";
import store from "@/store/index";
import { toast } from "vue3-toastify";
import { TENANCY_SETTINGS } from "@/config";
import {
  forceLogoutToLogin,
  isSessionExpiredResponse,
  PUBLIC_PATHS,
} from "@/auth/session";

const getCurrentLocale = () =>
  store.getters.currentLocale ||
  localStorage.getItem("currentLocale") ||
  axios.defaults.headers.common["Accept-Language"];

const AXIOS_CONFIG_KEYS = new Set([
  "baseURL",
  "url",
  "method",
  "headers",
  "params",
  "data",
  "timeout",
  "timeoutErrorMessage",
  "withCredentials",
  "adapter",
  "auth",
  "responseType",
  "responseEncoding",
  "xsrfCookieName",
  "xsrfHeaderName",
  "onUploadProgress",
  "onDownloadProgress",
  "maxContentLength",
  "maxBodyLength",
  "maxRedirects",
  "socketPath",
  "httpAgent",
  "httpsAgent",
  "proxy",
  "signal",
  "cancelToken",
  "decompress",
  "transitional",
  "env",
  "formSerializer",
  "validateStatus",
]);

const normalizeRequestConfig = (config = {}) => {
  if (!config || typeof config !== "object" || Array.isArray(config)) {
    return {};
  }

  const normalizedConfig = {};
  const normalizedHeaders = { ...(config.headers || {}) };

  Object.entries(config).forEach(([key, value]) => {
    if (key === "headers") {
      return;
    }

    if (AXIOS_CONFIG_KEYS.has(key)) {
      normalizedConfig[key] = value;
      return;
    }

    normalizedHeaders[key] = value;
  });

  if (Object.keys(normalizedHeaders).length) {
    normalizedConfig.headers = normalizedHeaders;
  }

  return normalizedConfig;
};

const ApiService = {
  async init(baseURL, tenantId, locale, availableLocales = []) {
    // Set headers and other configuration
    axios.defaults.headers.common["X-Tenant"] = tenantId.toUpperCase();
    this.setLocale(locale);
    this.setAvailableLocales(availableLocales);
    axios.defaults.baseURL = baseURL;

    // Set up interceptors
    this.setupInterceptors();
  },

  setupInterceptors() {
    axios.interceptors.request.use((config) => {
      store.dispatch("addPending", true);
      config.headers.common.Authorization = `Bearer ${TokenService.getToken()}`;
      return config;
    });

    axios.interceptors.response.use(
      (response) => {
        toast.clearAll();
        store.dispatch("addPending", false);
        if (response.config.method !== "get") {
          // this.showToast('success', 'message.toast.success')
        }
        return response;
      },
      (error) => {
        store.dispatch("addPending", false);

        if (isSessionExpiredResponse(error)) {
          const currentPath = router.currentRoute.value.path;
          const requestUrl = error.config?.url || "";
          const isAuthRequest =
            requestUrl.includes("auth/login") ||
            requestUrl.includes("auth/me") ||
            requestUrl.includes("users/logout");

          if (!PUBLIC_PATHS.includes(currentPath) && !isAuthRequest) {
            forceLogoutToLogin();
          }
        }

        return Promise.reject(error);
      }
    );
  },

  setHeader(contentType = "application/json") {
    axios.defaults.headers.common["Content-Type"] = contentType;
    axios.defaults.withCredentials = true;
  },

  removeHeader() {
    delete axios.defaults.headers.common["Authorization"];
    delete axios.defaults.headers.common["Content-Type"];
  },

  setLocale(locale) {
    if (!locale) {
      return;
    }

    axios.defaults.headers.common["Accept-Language"] = locale;
    localStorage.setItem("currentLocale", locale);
  },

  setAvailableLocales(locales = []) {
    if (!Array.isArray(locales) || !locales.length) {
      return;
    }

    const normalizedLocales = [...new Set(locales.filter(Boolean))];
    axios.defaults.headers.common["X-Available-Locales"] =
      normalizedLocales.join(",");
  },

  csrfCookie() {
    return axios.get("/sanctum/csrf-cookie");
  },

  request(method, resource, data = null, config = {}) {
    const apiLocale = getCurrentLocale() || "en-GB";
    this.setLocale(apiLocale);

    const urlPrefix = TENANCY_SETTINGS.TENANCY
      ? "/api/v1/dealer/"
      : `/api/v1/${apiLocale}/`;
    const url = urlPrefix + resource;
    return axios({
      method,
      url,
      data,
      ...normalizeRequestConfig(config),
    });
  },

  get(resource, config = {}) {
    return this.request("get", resource, null, config);
  },

  post(resource, data, headers) {
    return this.request("post", resource, data, headers);
  },

  logout(resource, data, headers) {
    return this.request("post", "/users/logout", data, headers);
  },

  put(resource, data) {
    return this.request("put", resource, data);
  },

  delete(resource, data = undefined) {
    return this.request("delete", resource, data);
  },

  patch(resource, data) {
    return this.request("patch", resource, data);
  },
};

export default ApiService;
